import { UploadUserType, CSVImportColumns } from 'utils/api/types';
import { UserFieldOption } from 'utils/api/modules/user/user.types';

export interface ImportCustomFieldColumn {
  slot: number;
  label: string;
}

/** Per-org headers and option lists, fetched once per modal open — never per row. */
export interface ImportVocabulary {
  departmentLabel: string;
  regionLabel: string;
  employmentTypeLabel: string;
  customFields: ImportCustomFieldColumn[];
  departmentOptions: UserFieldOption[];
  regionOptions: UserFieldOption[];
  employmentTypeOptions: UserFieldOption[];
  showObjectManager: boolean;
  showSite: boolean;
  showUnit: boolean;
  unitOptions: ImportUnitOption[];
  /** Vatix-staff cross-org import: this vocabulary belongs to the wrong organisation. */
  suppressed: boolean;
}

export interface ImportUnitOption {
  uuid: string;
  path: string[];
}

export type CustomFieldKey = 'customField1' | 'customField2' | 'customField3' | 'customField4' | 'customField5';

export const CUSTOM_FIELD_SLOTS = [1, 2, 3, 4, 5] as const;

export const customFieldKey = (slot: number): CustomFieldKey => `customField${slot}` as CustomFieldKey;

export const EMPTY_IMPORT_VOCABULARY: ImportVocabulary = {
  departmentLabel: 'Department',
  regionLabel: 'Region',
  employmentTypeLabel: 'Employment type',
  customFields: [],
  departmentOptions: [],
  regionOptions: [],
  employmentTypeOptions: [],
  showObjectManager: false,
  showSite: false,
  showUnit: false,
  unitOptions: [],
  suppressed: false,
};

export interface ImportTemplateColumn {
  key: keyof UploadUserType;
  header: string;
  example: string;
}

const firstOptionName = (options: UserFieldOption[]): string => options.find((option) => option.isActive)?.name ?? '';

const MANAGER_COLUMN: ImportTemplateColumn = {
  key: 'manager',
  header: CSVImportColumns.manager,
  example: '',
};

/**
 * Associated site has no example value on purpose — an invented site name would fail to resolve.
 * Line manager survives suppression: it is an email resolved server-side against the target org, so
 * unlike the option uuids it means the same thing on a cross-org import.
 */
export const importTemplateColumns = (vocabulary: ImportVocabulary): ImportTemplateColumn[] => {
  if (vocabulary.suppressed) {
    return [MANAGER_COLUMN];
  }

  const columns: ImportTemplateColumn[] = [
    MANAGER_COLUMN,
    { key: 'jobTitle', header: CSVImportColumns.jobTitle, example: 'Site Supervisor' },
    { key: 'employeeId', header: CSVImportColumns.employeeId, example: 'E-4471' },
    { key: 'startDate', header: CSVImportColumns.startDate, example: '01/04/2026' },
    { key: 'country', header: CSVImportColumns.country, example: 'United Kingdom' },
    {
      key: 'department',
      header: vocabulary.departmentLabel,
      example: firstOptionName(vocabulary.departmentOptions),
    },
    {
      key: 'employmentType',
      header: vocabulary.employmentTypeLabel,
      example: firstOptionName(vocabulary.employmentTypeOptions),
    },
  ];

  if (vocabulary.showObjectManager) {
    columns.push({
      key: 'region',
      header: vocabulary.regionLabel,
      example: firstOptionName(vocabulary.regionOptions),
    });
  }
  if (vocabulary.showSite) {
    columns.push({ key: 'site', header: CSVImportColumns.site, example: '' });
  }
  if (vocabulary.showUnit) {
    columns.push({ key: 'unit', header: CSVImportColumns.unit, example: '' });
  }
  if (vocabulary.showObjectManager) {
    vocabulary.customFields.forEach(({ slot, label }) =>
      columns.push({ key: customFieldKey(slot), header: label, example: 'Value' })
    );
  }

  return columns;
};

/** core-api-owned fields, which is what entitlement gating and suppression apply to. */
export const IMPORT_ATTRIBUTE_KEYS: (keyof UploadUserType)[] = [
  'jobTitle',
  'employeeId',
  'startDate',
  'country',
  'department',
  'employmentType',
  'region',
  'site',
  ...CUSTOM_FIELD_SLOTS.map(customFieldKey),
];

export const IMPORT_COLUMN_KEYS: (keyof UploadUserType)[] = [...IMPORT_ATTRIBUTE_KEYS, 'manager', 'unit'];
